import React from 'react';
import classNames from 'classnames';
import { Checkbox } from '@headlessui/react';

import { QuestionChoice } from 'types/Question';

import useLabelTranslation from 'hooks/useLabelTranslation';

import Icon from '../Icon';

interface MultipleChoiceCardActionsProps {
  choices: QuestionChoice[];
  value: string[];
  onChange: (value: string[]) => void;
}

const MultipleChoiceCardActions: React.FC<MultipleChoiceCardActionsProps> = ({ choices, value, onChange }) => {
  const { getLabelTranslation } = useLabelTranslation();

  const onClick = React.useCallback((choiceId: string) => {
    if (value?.includes(choiceId)) {
      const newValue = value.filter((id) => id !== choiceId);
      onChange?.(newValue.length > 0 ? newValue : undefined);
    } else {
      onChange?.([...(value || []), choiceId]);
    }
  }, [onChange, value]);

  return (
    <div className="flex flex-col items-center gap-4">
      {choices.map((choice) => {
        const checked = value.includes(choice.id);
        return (
          <div
            key={choice.id}
            data-testid={`choice-${choice.id}`}
            className={classNames(
              'group flex items-center justify-between w-full p-5 kiosk:p-10 bg-card-radio-bg rounded-card border-card-radio-border border-btn-primary-size cursor-pointer duration-500',
              {
                'has-hover:hover:border-btn-primary-hover-border': !checked,
                'has-hover:border-btn-primary-text': checked,
              },
            )}
            onClick={() => onClick(choice.id)}
          >
            <span className={classNames('text-left font-normal', checked && 'text-radio-secondary-active-text')}>
              {getLabelTranslation(choice.value)}
            </span>
            <Checkbox
              checked={checked}
              className={classNames(
                'cursor-pointer group size-6 border-radio-size border-radio-secondary-border flex items-center justify-center shrink-0 duration-500 rounded-sm',
                checked && 'bg-radio-secondary-active-bg border-radio-secondary-active-border!',
                !checked && 'group-hover:border-radio-secondary-hover-border',
              )}
            >
              {checked && <Icon className="fb-icon--check text-button-xs text-radio-secondary-active-dot" />}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};

export default MultipleChoiceCardActions;
